import { Component } from 'react';
import sharedStyles from '../single-page.module.scss';
import { FullscreenHost } from '../fullscreen-host.component';
import styles from './html-entity.component.module.scss';
import { PersistedInput } from '../../../utils/persisted-input';

interface HtmlEntityState {
    input: string;
    output: string;
    copied: boolean;
    error: string;
    encoder: boolean;
}

const ENCODE_MAP: [RegExp, string][] = [
    [/&/g, '&amp;'], [/</g, '&lt;'], [/>/g, '&gt;'],
    [/"/g, '&quot;'], [/'/g, '&#39;'],
    [/©/g, '&copy;'], [/®/g, '&reg;'], [/€/g, '&euro;'],
    [/£/g, '&pound;'], [/¥/g, '&yen;'], [/—/g, '&mdash;'],
    [/–/g, '&ndash;'], [/…/g, '&hellip;'], [/•/g, '&bull;'],
    [/™/g, '&trade;'], [/ /g, '&nbsp;'],
];

function encode(text: string): string {
    let result = text;
    for (const [re, entity] of ENCODE_MAP) result = result.replace(re, entity);
    // Encode remaining non-ASCII
    return result.replace(/[^\x20-\x7E\n\r\t]/g, ch => `&#${ch.charCodeAt(0)};`);
}

function decode(text: string): string {
    const el = document.createElement('textarea');
    el.innerHTML = text;
    return el.value;
}

export default class UhtmlUentityComponent extends Component<{ feature?: string }, HtmlEntityState> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    private saveTimer: ReturnType<typeof setTimeout> | null = null;

    private get storageKey(): string {
        // Persist separately per direction so encoder/decoder don't trample
        // each other's last typed value.
        return `html-entity:${this.props.feature || 'encoder'}`;
    }

    constructor(props: { feature?: string }) {
        super(props);
        const restored = PersistedInput.load(
            `html-entity:${props.feature || 'encoder'}`,
            { input: '' },
            (v: any) => !!v && typeof v.input === 'string',
        );
        const encoder = props.feature !== 'html-entity-decoder';
        this.state = { input: restored.input, output: '', copied: false, error: '', encoder };
    }

    componentDidMount() {
        // Re-run conversion for restored input now that the document is available.
        if (this.state.input) this.convert(this.state.input);
    }

    componentDidUpdate(_: any, prev: HtmlEntityState) {
        if (prev.input !== this.state.input) {
            if (this.saveTimer) clearTimeout(this.saveTimer);
            this.saveTimer = setTimeout(() =>
                PersistedInput.save(this.storageKey, { input: this.state.input }), 500);
        }
    }

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
        if (this.saveTimer) clearTimeout(this.saveTimer);
    }

    get isEncoder(): boolean {
        return this.state.encoder;
    }

    convert = (input: string) => {
        try {
            const output = this.isEncoder ? encode(input) : decode(input);
            this.setState({ input, output, error: '' });
        } catch {
            this.setState({ input, error: 'Conversion failed', output: '' });
        }
    };

    copy = () => {
        navigator.clipboard.writeText(this.state.output).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    swap = () => {
        this.setState(s => ({ encoder: !s.encoder, input: s.output, output: '', error: '' }), () => this.convert(this.state.input));
    };

    render() {
        const { input, output, copied, error } = this.state;
        const label = this.isEncoder ? 'Encode' : 'Decode';

        return (
            <FullscreenHost>
            <div className="row">
                <div className="col-12">
                    <div className={styles.grid}>
                        <div className={styles.headerRow}>
                            <label className={sharedStyles.label}>Input ({this.isEncoder ? 'Plain Text' : 'HTML Entities'})</label>
                            <button className={styles.swapButton} onClick={this.swap} title="Swap direction">⇄</button>
                            <div className={styles.outputLabel}>
                                <label className={sharedStyles.label}>{label}d Output</label>
                                {output && (
                                    <button className={`${sharedStyles.copyButton} ${copied ? sharedStyles.copyButtonSuccess : ''}`}
                                        onClick={this.copy}>{copied ? '✓ Copied' : 'Copy'}</button>
                                )}
                            </div>
                        </div>
                        <div>
                            <textarea className={sharedStyles.textarea} placeholder={this.isEncoder ? 'Enter text with <html> & "special" chars...' : 'Enter &lt;html&gt; &amp; entities...'}
                                value={input} onChange={e => this.convert(e.target.value)} rows={8} />
                        </div>
                        <div>
                            <div className={styles.readOnlyWrapper}>
                                <span className={styles.readOnlyBadge}>Read Only</span>
                                <textarea className={sharedStyles.textarea} value={output} readOnly rows={8} style={{ background: '#f1f5f9' }} />
                            </div>
                        </div>
                    </div>
                    {error && <div className={sharedStyles.errorMessage}>{error}</div>}
                    {output && (
                        <div className={sharedStyles.stats}>
                            <span>Input chars: <strong>{input.length}</strong></span>
                            <span>Output chars: <strong>{output.length}</strong></span>
                        </div>
                    )}
                </div>
            </div>

            </FullscreenHost>
        );
    }
}
